import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import cn from "classnames";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

import moment from "moment";
import {
  useAnnouncement,
  useDeleteAnnouncement,
  usePatchAnnouncement,
} from "../../api/noticeApi";
import { type Role } from "../../types/user";
import { getDepartmentName } from "../../utils/departmentUtils";
import { Pagination, usePagination } from "../../components/Pagination";

import "./notice.scss";

const Announcement = ({ role }: { role: Role }) => {
  const [announcements, setAnnouncements] = useState([]);
  const { pagination, setPage } = usePagination();
  const { data, isLoading } = useAnnouncement({ pagination });
  const { mutateAsync: deleteAnnouncement } = useDeleteAnnouncement();
  const { mutateAsync: updateAnnouncement } = usePatchAnnouncement();

  const maxPage = useMemo(() => {
    if (!data) return 0;
    return Math.ceil(data.total / 10);
  }, [data]);

  useEffect(() => {
    if (data) {
      setAnnouncements(data.announcements);
    }
  }, [data]);

  return (
    <div className="wideContent">
      {role === "staff" && (
        <Link to="new">
          <button className="moveButton">추가하기</button>
        </Link>
      )}
      {isLoading ? (
        <p>로딩중...</p>
      ) : announcements.length ? (
        <div>
          {announcements.map((announcement: any) => (
            <Link to={announcement.id.toString()}>
              <div
                key={announcement.id}
                className={cn("announcementTitle", {
                  priority: announcement.priority,
                })}
              >
                <p>
                  {announcement.priority && (
                    <>
                      <PushPinIcon fontSize="small" />{" "}
                    </>
                  )}
                  [{getDepartmentName(announcement.department)}]{" "}
                  {announcement.title}
                </p>
                <p>
                  {announcement.creator_name || "관리자"} /{" "}
                  {moment(announcement.created_at).format("YYYY-MM-DD LT")}
                </p>
                {role === "staff" && (
                  <div className="announcementTitleButtons">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        let priority;
                        if (announcement.priority) {
                          priority = null;
                        } else {
                          priority = prompt("우선순위를 입력하세요.");
                          if (priority) {
                            const parsed = parseInt(priority, 10);
                            if (
                              parsed.toString() !== priority ||
                              !Number.isInteger(parsed) ||
                              parsed < 1
                            ) {
                              alert("1 이상의 정수만 입력할 수 있습니다.");
                              return;
                            }
                          }
                        }

                        updateAnnouncement({
                          id: announcement.id,
                          data: { priority },
                        });
                      }}
                    >
                      {announcement.priority ? (
                        <>
                          <PushPinIcon /> {announcement.priority}
                        </>
                      ) : (
                        <PushPinOutlinedIcon />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (window.confirm("정말 삭제하시겠습니까?")) {
                          deleteAnnouncement({ id: announcement.id });
                        }
                      }}
                      className="warning"
                    >
                      <DeleteOutlineIcon />
                    </button>
                  </div>
                )}
              </div>
            </Link>
          ))}
          <Pagination
            pageIndex={pagination.page}
            setPageIndex={setPage}
            maxPage={maxPage}
          />
        </div>
      ) : (
        <p>등록된 공지사항이 없습니다.</p>
      )}
    </div>
  );
};

export default Announcement;
